import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import * as React from 'react'
import { DayPicker } from 'react-day-picker'
import { cs } from 'react-day-picker/locale'

import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * Kalendář (react-day-picker v9): česky, týden od pondělí, šipky v hlavičce vedle měsíce.
 * Vybraný den je plný značkovou barvou, dnešek má jemný podklad, dny mimo měsíc jsou tlumené.
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  // Rozbalovací rok jinak nabízí sto let dozadu. Doklady se týkají posledních let, proto 10 let zpět a rok dopředu.
  // Jen u rozbalovacího výběru, kalendář se šipkami nechává navigaci neomezenou.
  const year = new Date().getFullYear()
  return (
    <DayPicker
      locale={cs}
      weekStartsOn={1}
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      {...(captionLayout === 'label' ? {} : { startMonth: new Date(year - 10, 0), endMonth: new Date(year + 1, 11) })}
      className={cn('p-3', className)}
      classNames={{
        root: 'relative',
        months: 'relative flex flex-col gap-4 sm:flex-row',
        month: 'flex w-full flex-col gap-3',
        nav: 'absolute inset-x-0 top-0 flex items-center justify-between',
        button_previous: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 p-0 text-muted-foreground hover:text-foreground aria-disabled:opacity-40',
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 p-0 text-muted-foreground hover:text-foreground aria-disabled:opacity-40',
        ),
        month_caption: 'flex h-8 items-center justify-center px-9',
        caption_label:
          captionLayout === 'label'
            ? 'text-sm font-medium first-letter:uppercase'
            : 'flex h-8 items-center gap-1 rounded-md pl-2 pr-1.5 text-sm font-medium first-letter:uppercase [&>svg]:size-3.5 [&>svg]:text-muted-foreground',
        // Rozbalovací měsíc a rok: nativní <select> je průhledný přes popisek, takže je vidět jen popisek se šipkou.
        dropdowns: 'flex h-8 items-center justify-center gap-1.5',
        dropdown_root:
          'relative rounded-md has-focus:ring-[3px] has-focus:ring-ring/50 hover:bg-muted',
        dropdown: 'absolute inset-0 cursor-pointer opacity-0',
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday:
          'w-9 text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground',
        week: 'mt-1 flex w-full',
        day: cn(
          'relative size-9 p-0 text-center text-sm',
          props.mode === 'range' &&
            '[&:has([aria-selected])]:bg-brand-soft first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md',
        ),
        day_button:
          'inline-flex size-9 items-center justify-center rounded-md tabular transition-colors hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
        selected:
          '[&>button]:bg-primary [&>button]:font-medium [&>button]:text-primary-foreground [&>button]:hover:bg-primary/90',
        today:
          '[&>button]:font-semibold [&:not([aria-selected])>button]:bg-muted',
        outside:
          'text-muted-foreground/60 [&[aria-selected]>button]:bg-primary/70',
        disabled: 'pointer-events-none opacity-40',
        range_start: 'rounded-l-md bg-brand-soft',
        range_end: 'rounded-r-md bg-brand-soft',
        range_middle:
          '[&>button]:bg-transparent [&>button]:text-foreground [&>button]:hover:bg-muted',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chevronClass }) => {
          const Icon =
            orientation === 'left' ? ChevronLeft : orientation === 'right' ? ChevronRight : orientation === 'up' ? ChevronUp : ChevronDown
          return <Icon className={cn('size-4', chevronClass)} />
        },
      }}
      labels={{
        labelNext: () => 'Následující měsíc',
        labelPrevious: () => 'Předchozí měsíc',
        labelMonthDropdown: () => 'Vyberte měsíc',
        labelYearDropdown: () => 'Vyberte rok',
      }}
      {...props}
    />
  )
}

export { Calendar }
